/**
 * A yes to a draft's summary, however it's said: "looks good", "perfect", "lgtm", "ya looks gd", "yep snd it",
 * "perfect 👍". Only approval and politeness are allowed: "yes but make it urgent" or "send it to grade 4" is a change.
 */

/** Text-speak for the words below. */
const SPELLINGS: Record<string, string> = {
  ja: "yes", yebo: "yes", lekker: "great", ya: "yes", yah: "yes", yea: "yes", ye: "yes", yas: "yes", ys: "yes", y: "yes",
  gd: "good", gr8: "great", k: "ok", kk: "ok", oki: "ok", okey: "ok", okk: "ok",
  snd: "send", pls: "please", plz: "please", plse: "please", thx: "thanks", thnx: "thanks", tnx: "thanks", ty: "thanks", ta: "thanks",
  thats: "that's", its: "it's", lets: "let's",
};

/** One piece of approval, after the spellings are fixed. */
const PIECE = new RegExp(
  "^(?:" +
    [
      "yes|yep|yeah|yup|sure|ok|okay|alright|right|absolutely|definitely|indeed|shot|she says yes|he says yes",
      "perfect|great|good|cool|nice|lovely|brilliant|excellent|fine|fab|fabulous|awesome|wonderful|super|sweet|amazing|100",
      "correct|exactly|spot on|all good|all correct|all fine|all set|good to go|ready|done|approved?|confirm(?:ed)?",
      "that looks? (?:good|great|right|fine|perfect|ok|okay|correct)|(?:the|my|our) (?:notice|poll|event) looks? (?:good|great|right|fine|perfect|ok|okay|correct)|lgtm|looks? (?:good|great|right|fine|perfect|ok|okay|correct|lovely)|sounds? (?:good|great|fine|perfect|right|lovely)",
      "that'?s? (?:it|right|fine|perfect|great|good|correct|lovely|all)|it'?s (?:fine|perfect|good|great|right|correct)|that works|works for me|happy with (?:it|that|this)",
      "go ahead|go for it|do it|let'?s go|let'?s do it|ship it|open it|open it up|send|send it|publish it|post it",
    ].join("|") +
    ")$"
);

/** Words that only soften it: "great, thanks", "yes please", "perfect mate". */
const POLITE = /^(?:thanks|thank you|please|mate|ma'am|so much|then|now|now please|for me|cheers|a lot)$/;

export function isApproval(said: string): boolean {
  if (/^right[,\s]+(?:thanks|thank you)(?: so much)?[.!]*$/i.test(said.trim())) return false;
  const words = said
    .toLowerCase()
    .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}]/gu, " ")
    .replace(/[’`]/g, "'")
    .replace(/[^a-z0-9'%\s,]+/g, " ")
    .replace(/%/g, "")
    .split(/[\s,]+/)
    .filter(Boolean)
    .map((word) => SPELLINGS[word] ?? word);
  // "👍" alone is a thumbs up
  if (words.length === 0) return /[\u{1F44D}\u{2705}\u{1F44C}]/u.test(said);
  // Read greedily: the longest approval or politeness phrase at each point, until every word is used
  let at = 0;
  let approved = false;
  while (at < words.length) {
    let step = 0;
    for (let size = Math.min(4, words.length - at); size > 0; size -= 1) {
      const phrase = words.slice(at, at + size).join(" ");
      if (PIECE.test(phrase)) {
        approved = true;
        step = size;
        break;
      }
      if (POLITE.test(phrase) || (phrase === "and" && at > 0)) {
        step = size;
        break;
      }
    }
    if (step === 0) return false;
    at += step;
  }
  return approved;
}
